import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { isArxivUrl } from '@/lib/utils/arxiv-utils';
import { addPaperVersion, type VersionType } from '@/lib/api/paper-mutations/add-paper-version';
import { createPaper } from '@/lib/api/paper-mutations';
import { uploadPaperFile } from '@/lib/api/paper-storage';
import { loadDraftMetadata } from '@/lib/writer/draftMetadata';
import { useSourceExportEntitlement } from '@/hooks/useSourceExportEntitlement';
import type { PaperAuthor } from '@/types/paper';
import PublishDraftDialogBody, { type PublishDraftFormState } from './PublishDraftDialogBody';
import WriterGate from './WriterGate';

interface SubmitAsVersionButtonProps {
  projectId: string;
  /**
   * Paper this draft has already been published to, if any. When null the
   * button offers first publication ("Publish on PaperVerse") and can also
   * link the draft to an existing paper.
   */
  paperId?: string | null;
  projectTitle?: string;
}

interface EligiblePaper {
  id: string;
  title: string;
  current_version: string;
  external_url: string | null;
}

/** Sentinel understood by createPaper / extract-paper-data as "AI, fill this". */
const PENDING_ABSTRACT_SENTINEL = 'Pending AI extraction';

const emptyForm: PublishDraftFormState = {
  title: '',
  abstract: '',
  authors: '',
  keywords: '',
  category: '',
  visibility: 'public',
};

const titleFromProject = (projectTitle?: string) => {
  const cleaned = (projectTitle ?? '').replace(/[^\w\- ]+/g, '').trim();
  return cleaned.length > 2 ? cleaned : 'Untitled Paper';
};

const InnerButton: React.FC<SubmitAsVersionButtonProps> = ({
  projectId,
  paperId,
  projectTitle,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');
  const [versionType, setVersionType] = useState<VersionType>('minor');

  const [selectedPaperId, setSelectedPaperId] = useState<string | null>(paperId ?? null);
  const [papers, setPapers] = useState<EligiblePaper[]>([]);
  const [loadingPapers, setLoadingPapers] = useState(false);
  const [query, setQuery] = useState('');
  const [picking, setPicking] = useState(false);

  const [form, setForm] = useState<PublishDraftFormState>(emptyForm);
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const { reason: planReason } = useSourceExportEntitlement();

  const isFirstPublish = !paperId;

  useEffect(() => {
    setSelectedPaperId(paperId ?? null);
  }, [paperId]);

  const loadPapers = useCallback(async () => {
    setLoadingPapers(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setPapers([]);
        return;
      }
      const { data, error } = await supabase
        .from('papers')
        .select('id, title, current_version, external_url')
        .eq('author_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      // arXiv papers cannot receive manually uploaded PDF versions.
      setPapers(
        ((data ?? []) as EligiblePaper[]).filter(
          (p) => !p.external_url || !isArxivUrl(p.external_url),
        ),
      );
    } catch (err) {
      console.error('[Writer] Failed to load papers for submission', err);
      toast.error('Could not load your papers');
      setPapers([]);
    } finally {
      setLoadingPapers(false);
    }
  }, []);

  // Load the picker list only when the user explicitly asks for it.
  useEffect(() => {
    if (open && picking) void loadPapers();
  }, [open, picking, loadPapers]);

  // Prefill the publish form from the draft itself.
  useEffect(() => {
    if (!open || !isFirstPublish || picking) return;
    let cancelled = false;
    setLoadingPrefill(true);
    void (async () => {
      try {
        const meta = await loadDraftMetadata(projectId, projectTitle);
        if (cancelled) return;
        setForm((prev) => ({
          ...prev,
          title: prev.title || meta.title,
          abstract: prev.abstract || meta.abstract,
          authors: prev.authors || meta.authors.join(', '),
        }));
      } catch (err) {
        console.error('[Writer] Failed to prefill publish metadata', err);
      } finally {
        if (!cancelled) setLoadingPrefill(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isFirstPublish, picking, projectId, projectTitle]);

  const selectedPaper = papers.find((p) => p.id === selectedPaperId) ?? null;

  const linkProjectToPaper = async (id: string) => {
    const { error } = await supabase
      .from('writer_projects')
      .update({ published_paper_id: id })
      .eq('id', projectId);
    if (error) {
      console.error('[Writer] Failed to link project to paper', error);
      return false;
    }
    return true;
  };

  const choosePaper = async (id: string) => {
    setSelectedPaperId(id);
    setPicking(false);
    await linkProjectToPaper(id);
  };

  /** Fetch the latest compiled PDF as a File, or throw with a readable reason. */
  const fetchCompiledPdf = async (): Promise<File> => {
    const { data: signed, error: signErr } = await supabase.functions.invoke<{
      url: string | null;
      reason?: string;
    }>('writer-compile-proxy', {
      body: { action: 'signed_url', project_id: projectId },
    });
    if (signErr || !signed?.url) {
      throw new Error(
        signed?.reason === 'no_compile'
          ? 'No compiled PDF yet — accept an artifact and wait for the compile to succeed.'
          : signErr?.message ?? 'Compiled PDF unavailable',
      );
    }
    const res = await fetch(signed.url);
    if (!res.ok) throw new Error(`Failed to fetch compiled PDF (${res.status})`);
    const blob = await res.blob();
    const filename = `${(projectTitle ?? 'writer-draft')
      .replace(/[^\w\-]+/g, '_')
      .slice(0, 60)}.pdf`;
    return new File([blob], filename, { type: 'application/pdf' });
  };

  const handlePublishNewPaper = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('You need to be signed in to publish.');

      const file = await fetchCompiledPdf();

      toast.loading('Uploading PDF…', { id: 'writer-publish' });
      const fileUrl = await uploadPaperFile(file);
      toast.dismiss('writer-publish');
      if (!fileUrl) return; // uploadPaperFile surfaces its own error toast

      const typedTitle = form.title.trim();
      const typedAbstract = form.abstract.trim();
      const submitTitle = typedTitle || titleFromProject(projectTitle);
      const submitAbstract =
        typedAbstract.length >= 10 ? typedAbstract : PENDING_ABSTRACT_SENTINEL;
      const submitCategory = form.category.trim() || 'other';
      const keywords = form.keywords
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean);
      const authors: PaperAuthor[] = form.authors
        .split(',')
        .map((n) => n.trim())
        .filter(Boolean)
        .map((name, i) => ({
          id: `writer-${Date.now()}-${i}`,
          name,
          isPlatformUser: false,
          institution: '',
        }));

      const paper = await createPaper(
        submitTitle,
        submitAbstract,
        user.id,
        submitCategory,
        keywords,
        fileUrl,
        undefined,
        authors,
        form.visibility,
        false,
        [],
        undefined,
        // A title the user typed is ground truth even when the abstract is
        // left to extraction.
        typedTitle.length === 0,
      );

      if (!paper) return; // createPaper surfaces its own error toast

      const linked = await linkProjectToPaper(paper.id);
      if (!linked) {
        toast.warning('Paper published, but the draft could not be linked to it.');
      }
      setOpen(false);
      navigate(`/paper/${paper.id}`);
    } catch (err) {
      toast.dismiss('writer-publish');
      toast.error(err instanceof Error ? err.message : 'Publishing failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitVersion = async () => {
    if (submitting || !selectedPaperId) return;
    setSubmitting(true);
    try {
      const file = await fetchCompiledPdf();

      const changeNotes = notes
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const result = await addPaperVersion({
        paperId: selectedPaperId,
        file,
        changeNotes: changeNotes.length > 0 ? changeNotes : ['Submitted from Writer'],
        versionType,
      });

      if (!result.success) {
        // addPaperVersion already surfaces a toast on failure.
        return;
      }
      setOpen(false);
      navigate(`/paper/${selectedPaperId}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const showPicker = picking || (!selectedPaperId && !isFirstPublish);
  const showPublishForm = isFirstPublish && !picking && !selectedPaperId;
  const filtered = query.trim()
    ? papers.filter((p) => p.title.toLowerCase().includes(query.trim().toLowerCase()))
    : papers;

  const triggerLabel = selectedPaperId ? 'Publish new version' : 'Publish on PaperVerse';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="default" className="gap-2" aria-label={triggerLabel}>
          <Send className="h-4 w-4" aria-hidden />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {showPicker
              ? 'Choose the paper to update'
              : showPublishForm
                ? 'Publish on PaperVerse'
                : 'Publish new version'}
          </DialogTitle>
          <DialogDescription>
            {showPicker
              ? 'Pick one of your papers. The latest compiled PDF from this draft will be attached to it as a new version.'
              : showPublishForm
                ? 'The latest compiled PDF from this draft becomes a new paper. An AI review runs on it, subject to your credit balance.'
                : 'The latest compiled PDF from this draft will be attached to the paper as a new version. An AI review will run on the new version, subject to your credit balance.'}
          </DialogDescription>
        </DialogHeader>

        {showPicker ? (
          <div className="space-y-3 py-2">
            <div className="relative">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search your papers"
                aria-label="Search your papers"
                className="pl-8"
              />
            </div>

            {loadingPapers ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                {papers.length === 0
                  ? 'You have no papers that can take a new version yet — arXiv-imported papers cannot receive uploaded versions.'
                  : 'No paper matches that search.'}
              </p>
            ) : (
              <ul className="max-h-64 overflow-y-auto rounded-md border border-border divide-y divide-border">
                {filtered.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => void choosePaper(p.id)}
                      className="w-full text-left px-3 py-2.5 hover:bg-accent/50 transition"
                    >
                      <div className="text-sm font-medium line-clamp-2">{p.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        current v{p.current_version}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {isFirstPublish && (
              <button
                type="button"
                onClick={() => setPicking(false)}
                className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                Publish as a new paper instead
              </button>
            )}
          </div>
        ) : showPublishForm ? (
          <PublishDraftDialogBody
            form={form}
            setForm={setForm}
            loadingPrefill={loadingPrefill}
            showWatermarkNote={planReason === 'free'}
            onUseExistingPaper={() => setPicking(true)}
          />
        ) : (
          <div className="space-y-4 py-2">
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 flex items-start justify-between gap-3">
              <div className="text-sm">
                <div className="text-xs text-muted-foreground">Publishing to</div>
                <div className="font-medium line-clamp-2">
                  {selectedPaper?.title ?? 'Selected paper'}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs shrink-0"
                onClick={() => setPicking(true)}
              >
                Change
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Version bump</Label>
              <RadioGroup
                value={versionType}
                onValueChange={(v) => setVersionType(v as VersionType)}
                className="flex gap-4"
              >
                <label className="flex items-center gap-2 text-sm">
                  <RadioGroupItem value="minor" id="wr-vt-minor" />
                  Minor (typos, small edits)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <RadioGroupItem value="major" id="wr-vt-major" />
                  Major (new results, restructure)
                </label>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wr-changes">Change notes (one per line)</Label>
              <Textarea
                id="wr-changes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Rewrote introduction&#10;Added ablation table"
                rows={4}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          {!showPicker && (
            <Button
              onClick={showPublishForm ? handlePublishNewPaper : handleSubmitVersion}
              disabled={submitting}
              className="gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  {showPublishForm ? 'Publishing…' : 'Submitting…'}
                </>
              ) : showPublishForm ? (
                'Publish'
              ) : (
                'Submit'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SubmitAsVersionButton: React.FC<SubmitAsVersionButtonProps> = (props) => (
  <WriterGate>
    <InnerButton {...props} />
  </WriterGate>
);

export default SubmitAsVersionButton;
